import React, { useReducer, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Auth, { API_URL } from '../../utils/auth';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import TypingIndicator from './TypingIndicator';
import ProgressBar from './ProgressBar';
import InstructionCard from './InstructionCard';
import './OnboardingChat.css';

// ── Steps ──────────────────────────────────────────────
const STEPS = {
    WELCOME:              { inputType: 'none' },
    ASK_EMAIL:            { inputType: 'email',    placeholder: 'Enter your email',        field: 'email',           stepNum: 1 },
    ASK_PASSWORD:         { inputType: 'password', placeholder: 'Choose a password',        field: 'password',        stepNum: 2 },
    ASK_CONFIRM_PASSWORD: { inputType: 'password', placeholder: 'Confirm your password',    field: 'confirmPassword', stepNum: 3 },
    ASK_PHONE:            { inputType: 'tel',      placeholder: 'Enter your phone number',  field: 'phone',           stepNum: 4 },
    ASK_ORG_NAME:         { inputType: 'text',     placeholder: 'Enter your organization name', field: 'workspace_name', stepNum: 5 },
    REGISTERING:          { inputType: 'none' },
    REGISTRATION_SUCCESS: { inputType: 'none' },
    ASK_CIRCLE:           { inputType: 'buttons',  stepNum: 6, options: [
        { label: 'Yes', value: 'yes', variant: 'primary' },
        { label: 'No',  value: 'no',  variant: 'outline' },
    ]},
    ASK_CIRCLE_TOKEN:     { inputType: 'text', placeholder: 'Paste your Circle Admin v2 API Key', field: 'circleToken', stepNum: 6 },
    SAVING_CIRCLE_TOKEN:  { inputType: 'none' },
    CIRCLE_INSTRUCTIONS:  { inputType: 'confirm' },
    COMPLETE:             { inputType: 'none' },
    SKIP_CIRCLE_COMPLETE: { inputType: 'none' },
};

const TOTAL_STEPS = 6;

const BOT_MESSAGES = {
    WELCOME:              "Welcome to ClientBloom! I'll help you get set up in just a few minutes. Let's start with your account details.",
    ASK_EMAIL:            "What email address would you like to use for your account?",
    ASK_PASSWORD:         "Great! Now choose a secure password for your account. It should be at least 8 characters long.",
    ASK_CONFIRM_PASSWORD: "Please confirm your password.",
    ASK_PHONE:            "What's the best phone number to reach you?",
    ASK_ORG_NAME:         "What's the name of your organization?",
    REGISTERING:          null, // typing indicator only
    REGISTRATION_SUCCESS: "Your account is ready! Welcome aboard. Now let's connect your tools.",
    ASK_CIRCLE:           "Do you use Circle for your community?",
    ASK_CIRCLE_TOKEN:     "To connect Circle, I'll need your Admin v2 API Key. You can find this in your Circle admin settings.",
    SAVING_CIRCLE_TOKEN:  null, // typing indicator only
    CIRCLE_INSTRUCTIONS:  "Circle is connected! One last thing — please enable Community AI in your Circle settings:",
    COMPLETE:             "You're all set! Taking you to your dashboard now...",
    SKIP_CIRCLE_COMPLETE: "No problem! You can always connect Circle later from the Admin Panel. Taking you to your dashboard...",
};

// ── Validation ─────────────────────────────────────────
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(step, value, formData) {
    switch (step) {
        case 'ASK_EMAIL':
            if (!EMAIL_RE.test(value)) return 'Please enter a valid email address.';
            return null;
        case 'ASK_PASSWORD':
            if (value.length < 8) return 'Password must be at least 8 characters.';
            return null;
        case 'ASK_CONFIRM_PASSWORD':
            if (value !== formData.password) return "Passwords don't match. Please try again.";
            return null;
        case 'ASK_PHONE':
            if (!value.trim()) return 'Please enter your phone number.';
            return null;
        case 'ASK_ORG_NAME':
            if (!value.trim()) return 'Please enter your organization name.';
            return null;
        case 'ASK_CIRCLE_TOKEN':
            if (!value.trim()) return 'Please paste your Circle API key.';
            return null;
        default:
            return null;
    }
}

// ── Reducer ────────────────────────────────────────────
const initialState = {
    currentStep: 'WELCOME',
    messages: [],
    formData: { email: '', password: '', confirmPassword: '', phone: '', workspace_name: '', circleToken: '' },
    inputError: null,
    isTyping: false,
    showInput: false,
};

function reducer(state, action) {
    switch (action.type) {
        case 'ADD_BOT_MESSAGE':
            return { ...state, messages: [...state.messages, { id: Date.now(), sender: 'bot', content: action.payload }] };
        case 'ADD_BOT_COMPONENT':
            return { ...state, messages: [...state.messages, { id: Date.now(), sender: 'bot', component: action.payload }] };
        case 'ADD_USER_MESSAGE':
            return { ...state, messages: [...state.messages, { id: Date.now(), sender: 'user', content: action.payload }] };
        case 'SET_STEP':
            return { ...state, currentStep: action.payload, inputError: null, showInput: false, isTyping: false };
        case 'SET_TYPING':
            return { ...state, isTyping: action.payload };
        case 'SET_SHOW_INPUT':
            return { ...state, showInput: action.payload };
        case 'SET_INPUT_ERROR':
            return { ...state, inputError: action.payload };
        case 'SET_FORM_DATA':
            return { ...state, formData: { ...state.formData, ...action.payload } };
        default:
            return state;
    }
}

// ── Component ──────────────────────────────────────────
export default function OnboardingChat({ orgFromQuery }) {
    const [state, dispatch] = useReducer(reducer, {
        ...initialState,
        formData: { ...initialState.formData, workspace_name: orgFromQuery || '' },
    });
    const navigate = useNavigate();
    const messagesEndRef = useRef(null);
    const hasInitRef = useRef(false);

    // Auto-scroll
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [state.messages, state.isTyping]);

    // Helper: show typing then message, then input
    const showBotStep = useCallback((step) => {
        dispatch({ type: 'SET_STEP', payload: step });
        const msg = BOT_MESSAGES[step];

        if (msg === null) {
            // Typing-only steps (REGISTERING, SAVING_CIRCLE_TOKEN)
            dispatch({ type: 'SET_TYPING', payload: true });
            return;
        }

        dispatch({ type: 'SET_TYPING', payload: true });
        setTimeout(() => {
            dispatch({ type: 'SET_TYPING', payload: false });
            dispatch({ type: 'ADD_BOT_MESSAGE', payload: msg });

            // For instruction step, also add the card
            if (step === 'CIRCLE_INSTRUCTIONS') {
                dispatch({ type: 'ADD_BOT_COMPONENT', payload: 'instruction-card' });
            }

            dispatch({ type: 'SET_SHOW_INPUT', payload: true });
        }, 1000);
    }, []);

    // Init: Welcome -> first question
    useEffect(() => {
        if (hasInitRef.current) return;
        hasInitRef.current = true;

        dispatch({ type: 'SET_TYPING', payload: true });
        setTimeout(() => {
            dispatch({ type: 'SET_TYPING', payload: false });
            dispatch({ type: 'ADD_BOT_MESSAGE', payload: BOT_MESSAGES.WELCOME });
            setTimeout(() => showBotStep('ASK_EMAIL'), 1200);
        }, 1000);
    }, [showBotStep]);

    // Determine next step after a user input
    const advanceFrom = useCallback(async (step, value) => {
        switch (step) {
            case 'ASK_EMAIL':
                showBotStep('ASK_PASSWORD');
                break;
            case 'ASK_PASSWORD':
                showBotStep('ASK_CONFIRM_PASSWORD');
                break;
            case 'ASK_CONFIRM_PASSWORD':
                showBotStep('ASK_PHONE');
                break;
            case 'ASK_PHONE':
                if (orgFromQuery) {
                    // Skip org name, show acknowledgment, then register
                    dispatch({ type: 'SET_STEP', payload: 'REGISTERING' });
                    dispatch({ type: 'SET_TYPING', payload: true });
                    setTimeout(() => {
                        dispatch({ type: 'SET_TYPING', payload: false });
                        dispatch({ type: 'ADD_BOT_MESSAGE', payload: `I see you're joining ${orgFromQuery}! Setting up your account now...` });
                        doRegister();
                    }, 1000);
                } else {
                    showBotStep('ASK_ORG_NAME');
                }
                break;
            case 'ASK_ORG_NAME':
                doRegister();
                break;
            case 'ASK_CIRCLE':
                if (value === 'yes') {
                    showBotStep('ASK_CIRCLE_TOKEN');
                } else {
                    showBotStep('SKIP_CIRCLE_COMPLETE');
                    setTimeout(() => navigate('/'), 2500);
                }
                break;
            case 'ASK_CIRCLE_TOKEN':
                doSaveCircleToken(value);
                break;
            case 'CIRCLE_INSTRUCTIONS':
                showBotStep('COMPLETE');
                setTimeout(() => navigate('/'), 2500);
                break;
            default:
                break;
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgFromQuery, navigate, showBotStep]);

    // Registration API call
    const doRegister = useCallback(async () => {
        dispatch({ type: 'SET_STEP', payload: 'REGISTERING' });
        dispatch({ type: 'SET_TYPING', payload: true });

        // Small delay so the typing indicator feels natural
        await new Promise(r => setTimeout(r, 600));

        const { formData } = stateRef.current;
        const result = await Auth.register(
            formData.email,
            formData.password,
            formData.phone,
            formData.workspace_name
        );

        dispatch({ type: 'SET_TYPING', payload: false });

        if (result.success) {
            dispatch({ type: 'ADD_BOT_MESSAGE', payload: BOT_MESSAGES.REGISTRATION_SUCCESS });
            setTimeout(() => showBotStep('ASK_CIRCLE'), 1500);
        } else {
            const errorMsg = result.message || 'Registration failed. Please try again.';
            dispatch({ type: 'ADD_BOT_MESSAGE', payload: `Hmm, something went wrong: ${errorMsg}` });

            // If email conflict, go back to email step
            if (errorMsg.toLowerCase().includes('email')) {
                setTimeout(() => showBotStep('ASK_EMAIL'), 1500);
            } else {
                setTimeout(() => showBotStep('ASK_EMAIL'), 1500);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showBotStep]);

    // Circle token save
    const doSaveCircleToken = useCallback(async (token) => {
        dispatch({ type: 'SET_STEP', payload: 'SAVING_CIRCLE_TOKEN' });
        dispatch({ type: 'SET_TYPING', payload: true });

        try {
            const user = Auth.getUser();
            const workspaceId = Auth.getWorkspaceId();

            await axios.post(`${API_URL}/circle/circle-token`, {
                workspaceId: parseInt(workspaceId),
                adminUserId: user.id,
                botToken: token,
            });

            dispatch({ type: 'SET_TYPING', payload: false });
            dispatch({ type: 'ADD_BOT_MESSAGE', payload: BOT_MESSAGES.CIRCLE_INSTRUCTIONS });
            dispatch({ type: 'ADD_BOT_COMPONENT', payload: 'instruction-card' });
            dispatch({ type: 'SET_STEP', payload: 'CIRCLE_INSTRUCTIONS' });
            dispatch({ type: 'SET_SHOW_INPUT', payload: false }); // card has its own button
        } catch (err) {
            dispatch({ type: 'SET_TYPING', payload: false });
            const errorMsg = err.response?.data?.message || err.response?.data?.error || 'Failed to connect Circle. Please try again.';
            dispatch({ type: 'ADD_BOT_MESSAGE', payload: `Hmm, something went wrong: ${errorMsg}` });
            setTimeout(() => showBotStep('ASK_CIRCLE_TOKEN'), 1500);
        }
    }, [showBotStep]);

    // Keep a ref to state for async callbacks
    const stateRef = useRef(state);
    useEffect(() => { stateRef.current = state; }, [state]);

    // Handle user input submission
    const handleSubmit = useCallback((value) => {
        const { currentStep, formData } = stateRef.current;
        const stepConfig = STEPS[currentStep];

        // Validation
        const error = validate(currentStep, value, formData);
        if (error) {
            dispatch({ type: 'SET_INPUT_ERROR', payload: error });
            return;
        }

        dispatch({ type: 'SET_INPUT_ERROR', payload: null });
        dispatch({ type: 'SET_SHOW_INPUT', payload: false });

        // Display user's response as a message
        if (currentStep === 'ASK_PASSWORD' || currentStep === 'ASK_CONFIRM_PASSWORD') {
            dispatch({ type: 'ADD_USER_MESSAGE', payload: '••••••••' });
        } else if (currentStep === 'ASK_CIRCLE') {
            dispatch({ type: 'ADD_USER_MESSAGE', payload: value === 'yes' ? 'Yes' : 'No' });
        } else if (currentStep === 'ASK_CIRCLE_TOKEN') {
            // Show partial token for security
            const display = value.length > 8 ? value.slice(0, 4) + '••••' + value.slice(-4) : '••••••••';
            dispatch({ type: 'ADD_USER_MESSAGE', payload: display });
        } else if (currentStep !== 'CIRCLE_INSTRUCTIONS') {
            dispatch({ type: 'ADD_USER_MESSAGE', payload: value });
        }

        // Save form data
        if (stepConfig?.field) {
            dispatch({ type: 'SET_FORM_DATA', payload: { [stepConfig.field]: value } });
        }

        // Advance
        advanceFrom(currentStep, value);
    }, [advanceFrom]);

    // Handle instruction card "Done" button
    const handleInstructionDone = useCallback(() => {
        dispatch({ type: 'ADD_USER_MESSAGE', payload: "Done, I've enabled it!" });
        showBotStep('COMPLETE');
        setTimeout(() => navigate('/'), 2500);
    }, [showBotStep, navigate]);

    const stepConfig = STEPS[state.currentStep] || {};
    const stepNum = stepConfig.stepNum || 0;

    return (
        <div className="oc-container">
            <ProgressBar current={stepNum} total={TOTAL_STEPS} />

            <div className="oc-messages">
                {state.messages.map((msg) => (
                    <ChatMessage key={msg.id} sender={msg.sender}>
                        {msg.component === 'instruction-card'
                            ? <InstructionCard onDone={handleInstructionDone} />
                            : msg.content
                        }
                    </ChatMessage>
                ))}

                {state.isTyping && <TypingIndicator />}
                <div ref={messagesEndRef} />
            </div>

            {state.showInput && (
                <ChatInput
                    inputType={stepConfig.inputType}
                    placeholder={stepConfig.placeholder}
                    onSubmit={handleSubmit}
                    error={state.inputError}
                    options={stepConfig.options}
                />
            )}
        </div>
    );
}
